import { BaseComponent } from '../base-component.js';
import { api } from '../../client/api.js';
import { escapeHtml, escapeAttribute, stripHtml } from '../../shared/utils.js';
import type { Link } from '../../shared/types.js';
import componentStyles from './link-manager.css?inline';
import { toast } from '../../client/toast.js';
import { session } from '../../client/session.js';

export class LinkManager extends BaseComponent {
  private links: Link[] = [];
  private loading = true;
  private editingId: string | null = null;
  private draggedId: string | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.loadLinks();
  }

  protected styles(): string {
    return componentStyles;
  }

  protected template(): string {
    const user = session.getUser();
    const baseUrl = window.location.origin;
    const profileUrl = user ? `${baseUrl}/u/${encodeURIComponent(user.slug)}` : '';
    if (this.loading) {
      return '<app-loader></app-loader>';
    }

    return `
      <ag-panel>
        <ag-panel-header>
          <h2>Your Links</h2>
          <p class="description">
            Add the important links of your digital life to your profile, so no one needs to wonder how to find out about you and your world. Links like your social media accounts, your art portfolio, your online shop, or any other content you want to share are great starting points. These links will appear on your public profile page <a class="link" href="${profileUrl}">here</a>.
          </p>
        </ag-panel-header>
        <ag-panel-content>
          
          <h3>Add New Link</h3>
          <form class="add-form" id="addForm">
            <input type="text" name="title" placeholder="My Portfolio" required>
            <input type="url" name="url" placeholder="https://myportfolio.com" required>
            <button type="submit">Add Link</button>
          </form>
          
          ${this.links.length === 0
        ? '<div class="empty">No links yet. Add your first link above!</div>'
        : `<ul class="links-list">${this.renderLinks()}</ul>`
      }
        </ag-panel-content>
      </ag-panel>
    `;
  }

  private renderLinks(): string {
    return this.links
      .sort((a, b) => a.order - b.order)
      .map((link) => {
        const safeId = escapeAttribute(link.id);
        const safeTitle = escapeAttribute(link.title);
        const safeUrl = escapeAttribute(link.url);

        if (this.editingId === link.id) {
          return `
            <li class="link-item" data-id="${safeId}">
              <span class="drag-handle">⋮⋮</span>
              <form class="edit-form" data-id="${safeId}">
                <input type="text" name="title" value="${safeTitle}" required>
                <input type="url" name="url" value="${safeUrl}" required>
                <ag-button-group>
                  <button class="btn btn-success" type="submit">Save</button>
                  <button type="button" class="btn btn-secondary cancel-edit">Cancel</button>
                </ag-button-group>
              </form>
            </li>
          `;
        }

        return `
          <li class="link-item" data-id="${safeId}" draggable="true">
            <span class="drag-handle">⋮⋮</span>
            <div class="link-content">
              <div class="link-title">${escapeHtml(link.title)}</div>
              <div class="link-url">${escapeHtml(link.url)}</div>
            </div>
            <button class="toggle ${link.active ? 'active' : ''}" data-id="${safeId}" title="Toggle visibility"></button>
            <div class="ag-button-group">
              <button class="btn btn-primary edit" data-id="${safeId}">Edit</button>
              <button class="btn btn-danger delete" data-id="${safeId}">Delete</button>
            </div>
          </li>
        `;
      })
      .join('');
  }

  protected afterRender(): void {
    const addForm = this.$('#addForm') as HTMLFormElement;
    addForm?.addEventListener('submit', (e) => this.handleAdd(e));

    this.$all('.edit').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.editingId = btn.getAttribute('data-id');
        this.render();
        this.afterRender();
      });
    });

    this.$all('.delete').forEach((btn) => {
      btn.addEventListener('click', () => this.handleDelete(btn.getAttribute('data-id')!));
    });

    this.$all('.toggle').forEach((btn) => {
      btn.addEventListener('click', () => this.handleToggle(btn.getAttribute('data-id')!));
    });

    this.$all('.edit-form').forEach((form) => {
      form.addEventListener('submit', (e) => this.handleEdit(e));
    });

    this.$all('.cancel-edit').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.editingId = null;
        this.render();
        this.afterRender();
      });
    });

    this.$all('.link-item[draggable="true"]').forEach((item) => {
      item.addEventListener('dragstart', (e) => this.handleDragStart(e as DragEvent));
      item.addEventListener('dragend', () => this.handleDragEnd());
      item.addEventListener('dragover', (e) => this.handleDragOver(e as DragEvent));
      item.addEventListener('drop', (e) => this.handleDrop(e as DragEvent));
      item.addEventListener('dragleave', (e) => this.handleDragLeave(e as DragEvent));
    });
  }

  private async loadLinks(): Promise<void> {
    this.loading = true;
    const result = await api.getLinks();
    if (result.success && result.data) {
      this.links = result.data;
    }
    this.loading = false;
    this.render();
    this.afterRender();
  }

  private async handleAdd(e: Event): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const title = stripHtml(formData.get('title') as string);
    const url = formData.get('url') as string;

    const result = await api.createLink({ title, url });
    if (result.success && result.data) {
      this.links.push(result.data);
      form.reset();
      toast.success('Link added successfully!');
      this.render();
      this.afterRender();
    } else {
      toast.error('Failed to add link. Please try again.');
    }
  }

  private async handleEdit(e: Event): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const id = form.getAttribute('data-id')!;
    const formData = new FormData(form);

    const title = stripHtml(formData.get('title') as string);
    const url = formData.get('url') as string;

    const result = await api.updateLink(id, { title, url });
    if (result.success && result.data) {
      const index = this.links.findIndex((l) => l.id === id);
      if (index !== -1) {
        this.links[index] = result.data;
      }
      this.editingId = null;
      toast.success('Link updated successfully!');
      this.render();
      this.afterRender();
    } else {
      toast.error('Failed to update link. Please try again.');
    }
  }

  private async handleDelete(id: string): Promise<void> {
    if (!confirm('Delete this link?')) return;

    const result = await api.deleteLink(id);
    if (result.success) {
      this.links = this.links.filter((l) => l.id !== id);
      toast.success('Link deleted successfully!');
      this.render();
      this.afterRender();
    } else {
      toast.error('Failed to delete link. Please try again.');
    }
  }

  private async handleToggle(id: string): Promise<void> {
    const link = this.links.find((l) => l.id === id);
    if (!link) return;

    const result = await api.updateLink(id, { active: !link.active });
    if (result.success && result.data) {
      const index = this.links.findIndex((l) => l.id === id);
      if (index !== -1) {
        this.links[index] = result.data;
      }
      const status = result.data.active ? 'visible' : 'hidden';
      toast.success(`Link ${status} successfully!`);
      this.render();
      this.afterRender();
    } else {
      toast.error('Failed to toggle link. Please try again.');
    }
  }

  private handleDragStart(e: DragEvent): void {
    const item = e.target as HTMLElement;
    this.draggedId = item.getAttribute('data-id');
    item.classList.add('dragging');
  }

  private handleDragEnd(): void {
    this.draggedId = null;
    this.$all('.link-item').forEach((item) => {
      item.classList.remove('dragging', 'drag-over');
    });
  }

  private handleDragOver(e: DragEvent): void {
    e.preventDefault();
    const item = (e.target as HTMLElement).closest('.link-item');
    if (item && item.getAttribute('data-id') !== this.draggedId) {
      item.classList.add('drag-over');
    }
  }

  private handleDragLeave(e: DragEvent): void {
    const item = (e.target as HTMLElement).closest('.link-item');
    item?.classList.remove('drag-over');
  }

  private async handleDrop(e: DragEvent): Promise<void> {
    e.preventDefault();
    const item = (e.target as HTMLElement).closest('.link-item') as HTMLElement;
    if (!item || !this.draggedId) return;

    const targetId = item.getAttribute('data-id');
    if (targetId === this.draggedId) return;

    const draggedIndex = this.links.findIndex((l) => l.id === this.draggedId);
    const targetIndex = this.links.findIndex((l) => l.id === targetId);

    const [draggedItem] = this.links.splice(draggedIndex, 1);
    this.links.splice(targetIndex, 0, draggedItem);

    this.links.forEach((link, index) => {
      link.order = index;
    });

    const linkIds = this.links.map((l) => l.id);
    const result = await api.reorderLinks(linkIds);

    if (result.success) {
      toast.success('Links reordered successfully!');
    } else {
      toast.error('Failed to reorder links. Please try again.');
    }

    this.render();
    this.afterRender();
  }
}

customElements.define('link-manager', LinkManager);
